/*
 * BandPreview - SVG band visualization with calibrated geometry.
 *
 * Renders the colored zones (back), the band PNG (middle, has transparent
 * dome cutouts), shine layer, and tap zones (top, conditional on mode).
 *
 * Requires `band-lit-v3.png` to be served from /band-lit-v3.png (i.e. in /public).
 */

const HEX = {
  red: "#FF3B30", orange: "#FF9500", yellow: "#FFD60A",
  green: "#30D158", cyan: "#64D2FF", blue: "#0A84FF",
  purple: "#BF5AF2", pink: "#FF4FC8", white: "#F2F2F7",
  off: "#1C1C1E",
};
const UNLIT = "#3c3f50";

// Calibrated geometry (dialed in via the prototype's calibration tool).
// Coords are in the 860x1500 viewBox of the band image.
const GEOM = {
  outer: { cx: 433, cy: 734, rx: 336, ry: 343, sw: 100 },
  inner: { cx: 433, cy: 752, r: 185 },
  five:  { cx: 433, cy: 734, rx: 336, ry: 343, sw: 100 },
};

const BandPreview = ({ mode, colors, onZoneTap }) => {
  // Resolve zone colors based on the current mode
  let outerColor = UNLIT;
  let innerColor = UNLIT;
  let show5 = false;
  let zone5 = {};

  if (mode === "1color") {
    outerColor = HEX[colors.outer];
    innerColor = HEX[colors.outer];
  } else if (mode === "2color") {
    outerColor = HEX[colors.outer];
    innerColor = HEX[colors.inner];
  } else if (mode === "5color") {
    outerColor = "transparent";
    innerColor = HEX[colors.c];
    show5 = true;
    zone5 = {
      tl: HEX[colors.tl],
      tr: HEX[colors.tr],
      bl: HEX[colors.bl],
      br: HEX[colors.br],
    };
  }

  const fiveEllipseProps = {
    cx: GEOM.five.cx, cy: GEOM.five.cy,
    rx: GEOM.five.rx, ry: GEOM.five.ry,
    strokeWidth: GEOM.five.sw,
    fill: "none",
  };

  return (
    <div className="ds-band-container">
      {/* Layer 1: colored zones (back) */}
      <svg className="ds-band-layer" viewBox="0 0 860 1500" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="bp-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="bp-clip-tl"><rect x="0"   y="0"   width="433" height="734" /></clipPath>
          <clipPath id="bp-clip-tr"><rect x="433" y="0"   width="427" height="734" /></clipPath>
          <clipPath id="bp-clip-br"><rect x="433" y="734" width="427" height="766" /></clipPath>
          <clipPath id="bp-clip-bl"><rect x="0"   y="734" width="433" height="766" /></clipPath>
        </defs>
        <g filter="url(#bp-glow)">
          <ellipse {...GEOM.outer} fill="none" stroke={outerColor} strokeWidth={GEOM.outer.sw} />
          {show5 && (
            <>
              <ellipse {...fiveEllipseProps} stroke={zone5.tl} clipPath="url(#bp-clip-tl)" />
              <ellipse {...fiveEllipseProps} stroke={zone5.tr} clipPath="url(#bp-clip-tr)" />
              <ellipse {...fiveEllipseProps} stroke={zone5.br} clipPath="url(#bp-clip-br)" />
              <ellipse {...fiveEllipseProps} stroke={zone5.bl} clipPath="url(#bp-clip-bl)" />
            </>
          )}
          <circle cx={GEOM.inner.cx} cy={GEOM.inner.cy} r={GEOM.inner.r} fill={innerColor} />
        </g>
      </svg>

      {/* Layer 2: band PNG (has transparent dome cutouts so colors below shine through) */}
      <img className="ds-band-image" src="/band-lit-v3.png" alt="Magic Band" />

      {/* Layer 3: shine (specular highlight on inner dome) */}
      <svg className="ds-band-layer ds-band-shine" viewBox="0 0 860 1500" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="bp-spec" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#fff" stopOpacity="0.7" />
            <stop offset="60%"  stopColor="#fff" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="395" cy="700" rx="50" ry="36" fill="url(#bp-spec)" />
      </svg>

      {/* Layer 4: tap zones (only for multi-color modes) */}
      {mode === "2color" && (
        <svg className="ds-band-layer ds-band-interact" viewBox="0 0 860 1500" preserveAspectRatio="xMidYMid meet">
          <ellipse className="ds-band-hit" cx="433" cy="734" rx="380" ry="395"
                   onClick={() => onZoneTap("outer")} />
          <circle  className="ds-band-hit" cx="433" cy="752" r="195"
                   onClick={() => onZoneTap("inner")} />
        </svg>
      )}
      {mode === "5color" && (
        <svg className="ds-band-layer ds-band-interact" viewBox="0 0 860 1500" preserveAspectRatio="xMidYMid meet">
          <rect   className="ds-band-hit" x="0"   y="0"   width="433" height="734" onClick={() => onZoneTap("tl")} />
          <rect   className="ds-band-hit" x="433" y="0"   width="427" height="734" onClick={() => onZoneTap("tr")} />
          <rect   className="ds-band-hit" x="433" y="734" width="427" height="766" onClick={() => onZoneTap("br")} />
          <rect   className="ds-band-hit" x="0"   y="734" width="433" height="766" onClick={() => onZoneTap("bl")} />
          <circle className="ds-band-hit" cx="433" cy="752" r="195" onClick={() => onZoneTap("c")} />
        </svg>
      )}
    </div>
  );
};

export default BandPreview;
